import {
  diag,
  DiagConsoleLogger,
  DiagLogLevel,
  trace,
  context,
  SpanStatusCode
} from '@opentelemetry/api';
import { BatchSpanProcessor } from '@opentelemetry/sdk-trace-base';
import {
  WebTracerProvider,
  StackContextManager
} from '@opentelemetry/sdk-trace-web';
import { W3CTraceContextPropagator } from '@opentelemetry/core';
import { resourceFromAttributes } from '@opentelemetry/resources';
import {
  ATTR_SERVICE_NAME,
  ATTR_SERVICE_VERSION
} from '@opentelemetry/semantic-conventions';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import {
  getOtelAuthorization,
  getOtelEnvironment,
  getOtelServiceName,
  getOtelServiceVersion,
  getOtelTracesUrl,
  isOtelConfigured,
  OTEL_ATTR_DEPLOYMENT_ENVIRONMENT
} from './config';
import { SessionParentSpanProcessor } from './sessionTrace';
import {
  isPollQuiet,
  isPollDrop,
  OTEL_ATTR_CRON_POLL,
  OTEL_ATTR_CRON_POLL_DROP,
  holdCronPollSpan,
  setCronPollFlushSink
} from './pollQuiet';

const ExportResultCode = { SUCCESS: 0, FAILED: 1 };

/** Span attribute key for the Paperfly request id from `/r=` (or `?r=`). */
export const OTEL_ATTR_REQUEST_R = 'request_id';

/** Ensure `Authorization: Bearer <token>` (otel.paperfly.io rejects raw tokens). */
function normalizeBearerAuthorization(value) {
  if (!value) return '';
  const trimmed = String(value).trim();
  if (!trimmed || trimmed.includes('${')) return '';
  if (/^Bearer\s+/i.test(trimmed)) return trimmed;
  return `Bearer ${trimmed}`;
}

/**
 * Read request id from Paperfly URL (`/r=…`, `/t=…`, or `?r=`).
 */
export function getRequestParamR(href) {
  const url =
    href || (typeof window !== 'undefined' ? window.location.href : '');
  if (!url) return '';

  const pathMatch = url.match(/\/r=([^&#/?]*)/i);
  if (pathMatch && pathMatch[1]) {
    try {
      return decodeURIComponent(pathMatch[1]);
    } catch (e) {
      return pathMatch[1];
    }
  }

  const pathMatchT = url.match(/\/t=([^&#/?]*)/i);
  if (pathMatchT && pathMatchT[1]) {
    try {
      return decodeURIComponent(pathMatchT[1]);
    } catch (e) {
      return pathMatchT[1];
    }
  }

  try {
    const parsed = new URL(url);
    const fromQuery = parsed.searchParams.get('r');
    if (fromQuery) return fromQuery;
  } catch (e) {
    // ignore invalid URL
  }
  return '';
}

/** Tags every recording span with the current URL `r`/`t` parameter when present. */
class RequestParamRSpanProcessor {
  onStart(span) {
    const r = getRequestParamR();
    if (r) {
      span.setAttribute(OTEL_ATTR_REQUEST_R, r);
    }
  }

  onEnd() {}

  forceFlush() {
    return Promise.resolve();
  }

  shutdown() {
    return Promise.resolve();
  }
}

/** Tags every recording span with pwa.session_id (same id as S3 error logs). */
class SessionIdSpanProcessor {
  onStart(span) {
    try {
      const tracing = require('./tracing');
      const sessionId = tracing.getSessionId && tracing.getSessionId();
      if (sessionId) {
        span.setAttribute('pwa.session_id', sessionId);
      } else if (typeof window !== 'undefined' && window.pwaSession) {
        span.setAttribute('pwa.session_id', window.pwaSession);
      }
    } catch (e) {
      if (typeof window !== 'undefined' && window.pwaSession) {
        span.setAttribute('pwa.session_id', window.pwaSession);
      }
    }
  }

  onEnd() {}

  forceFlush() {
    return Promise.resolve();
  }

  shutdown() {
    return Promise.resolve();
  }
}

const OTEL_ATTR_ERROR_MESSAGE = 'error.message';
const OTEL_ATTR_ERROR_TYPE = 'error.type';

function networkErrorMessage() {
  return navigator.onLine
    ? 'Network Error: request failed (CORS or connectivity)'
    : 'Network Error: offline';
}

/** Errors from scripts injected by the browser or extensions, not from our bundle. */
const BROWSER_NOISE_MESSAGES = [
  /__firefox__/,
  /window\.ethereum/,
  /webkit\.messageHandlers/,
  /__gCrWeb/
];

/**
 * `Script error.` without filename is a cross-origin script (no details);
 * the patterns above are Firefox iOS reader, wallet extensions, WebView bridges.
 */
export function isBrowserNoiseError(message, filename) {
  const msg = String(message || '').trim();
  if (/^(Uncaught )?Script error\.?$/i.test(msg) && !filename) return true;
  return BROWSER_NOISE_MESSAGES.some(re => re.test(msg));
}

/** Expected HTTP answers: keep the status code on the span, but not as ERROR. */
const EXPECTED_HTTP_STATUSES = [
  // Link to a deleted / unknown request — Load shows "not found".
  { url: /\/requesteddocument\/list(?:[?#]|$)/i, status: 404 }
];

export function isExpectedHttpError(url, status, responseText) {
  // EqxAPI refreshes the token and repeats the call.
  if (
    status === 401 &&
    String(responseText || '').indexOf('The incoming token has expired') !== -1
  ) {
    return true;
  }
  return EXPECTED_HTTP_STATUSES.some(
    e => e.status === status && e.url.test(String(url || ''))
  );
}

function readXhrResponseText(xhr) {
  try {
    return xhr.responseType === '' || xhr.responseType === 'text'
      ? xhr.responseText
      : '';
  } catch (e) {
    return '';
  }
}

function enrichHttpErrorSpan(span, message) {
  if (!message) return;
  span.setAttribute(OTEL_ATTR_ERROR_MESSAGE, message);
  span.setStatus({ code: SpanStatusCode.ERROR, message });
}

function enrichXhrErrorSpan(span, xhr) {
  const status = xhr.status;
  if (status === 0) {
    enrichHttpErrorSpan(span, networkErrorMessage());
    return;
  }
  if (status >= 400) {
    if (
      isExpectedHttpError(xhr.responseURL, status, readXhrResponseText(xhr))
    ) {
      return;
    }
    const text = (xhr.statusText || '').trim();
    enrichHttpErrorSpan(span, text ? `${status} ${text}` : String(status));
  }
}

function enrichFetchErrorSpan(span, result) {
  if (typeof Response !== 'undefined' && result instanceof Response) {
    if (result.status >= 400) {
      if (isExpectedHttpError(result.url, result.status)) return;
      const text = (result.statusText || '').trim();
      enrichHttpErrorSpan(
        span,
        text ? `${result.status} ${text}` : String(result.status)
      );
    }
    return;
  }
  const err = result || {};
  if (err.message) {
    enrichHttpErrorSpan(span, err.message);
    return;
  }
  if (err.status === 0 || err.status == null) {
    enrichHttpErrorSpan(span, networkErrorMessage());
  } else if (err.status >= 400) {
    enrichHttpErrorSpan(span, String(err.status));
  }
}

/** Refine timeout / generic XHR error messages after instrumentation. */
class HttpErrorMessageSpanProcessor {
  onStart() {}

  onEnding(span) {
    const attrs = span.attributes || {};
    const errorType = attrs[OTEL_ATTR_ERROR_TYPE];
    let message =
      typeof attrs[OTEL_ATTR_ERROR_MESSAGE] === 'string'
        ? attrs[OTEL_ATTR_ERROR_MESSAGE]
        : '';

    if (errorType === 'timeout') {
      message = 'Network Error: timeout';
      span.setAttribute(OTEL_ATTR_ERROR_MESSAGE, message);
    } else if (errorType === 'error' && (!message || message === 'error')) {
      message = networkErrorMessage();
      span.setAttribute(OTEL_ATTR_ERROR_MESSAGE, message);
    }

    if (message && span.status && span.status.code === SpanStatusCode.ERROR) {
      span.setStatus({ code: SpanStatusCode.ERROR, message });
    }
  }

  onEnd() {}

  forceFlush() {
    return Promise.resolve();
  }

  shutdown() {
    return Promise.resolve();
  }
}

/**
 * OTLP/HTTP JSON exporter (same approach as ts-switch).
 * Stock OTLPTraceExporter was producing 401s against otel.paperfly.io from CRA.
 */
class JsonOtlpTraceExporter {
  constructor(url, authorization) {
    this.url = url;
    this.authorization = authorization;
  }

  export(spans, resultCallback) {
    let batch = spans;
    try {
      const sessionTrace = require('./sessionTrace');
      if (
        sessionTrace.shouldInjectSessionRoot &&
        sessionTrace.shouldInjectSessionRoot(batch)
      ) {
        const root =
          sessionTrace.buildSessionRootExportSpan &&
          sessionTrace.buildSessionRootExportSpan();
        if (root) {
          batch = [root, ...batch];
          sessionTrace.markSessionRootExported &&
            sessionTrace.markSessionRootExported();
        }
      }
    } catch (e) {
      // ignore
    }

    try {
      const tracing = require('./tracing');
      if (tracing.buildOpenJourneyExportSpans) {
        const openJourneys = tracing.buildOpenJourneyExportSpans(batch);
        if (openJourneys && openJourneys.length) {
          batch = [...openJourneys, ...batch];
          tracing.markOpenJourneysExported &&
            tracing.markOpenJourneysExported(openJourneys);
        }
      }
    } catch (e) {
      // ignore
    }

    const body = JSON.stringify(toOtlpJson(batch));
    const headers = {
      'Content-Type': 'application/json'
    };
    if (this.authorization) {
      headers.Authorization = this.authorization;
    }

    void (async () => {
      try {
        const res = await fetch(this.url, {
          method: 'POST',
          headers,
          body,
          keepalive: true
        });
        if (res.ok || res.status === 204) {
          resultCallback({ code: ExportResultCode.SUCCESS });
          return;
        }
        if (process.env.REACT_APP_ENV !== 'production') {
          let detail = '';
          try {
            detail = await res.text();
          } catch (e) {
            // ignore
          }
          console.warn(
            '[otel] export failed',
            res.status,
            detail.slice(0, 200)
          );
        }
        resultCallback({ code: ExportResultCode.FAILED });
      } catch (e) {
        if (process.env.REACT_APP_ENV !== 'production') {
          console.warn('[otel] export error', e);
        }
        resultCallback({ code: ExportResultCode.FAILED });
      }
    })();
  }

  shutdown() {
    return Promise.resolve();
  }
}

function hrTimeToNano(hr) {
  return `${hr[0]}${String(hr[1]).padStart(9, '0')}`;
}

function toOtlpAnyValue(key, value) {
  if (typeof value === 'string') {
    return { key, value: { stringValue: value } };
  }
  if (typeof value === 'number') {
    if (Number.isInteger(value)) {
      return { key, value: { intValue: value } };
    }
    return { key, value: { doubleValue: value } };
  }
  if (typeof value === 'boolean') {
    return { key, value: { boolValue: value } };
  }
  return { key, value: { stringValue: String(value) } };
}

function toOtlpJson(spans) {
  const otlpSpans = spans.map(span => {
    const statusCode =
      span.status && span.status.code === 2
        ? 2
        : span.status && span.status.code === 1
        ? 1
        : 0;
    const parentSpanId =
      span.parentSpanId ||
      (span.parentSpanContext && span.parentSpanContext.spanId) ||
      undefined;
    const events = (span.events || []).map(event => ({
      name: event.name,
      timeUnixNano: hrTimeToNano(event.time),
      attributes: Object.entries(event.attributes || {}).map(([key, value]) =>
        toOtlpAnyValue(key, value)
      )
    }));
    return {
      spanId: span.spanContext().spanId,
      traceId: span.spanContext().traceId,
      name: span.name,
      kind: span.kind != null ? span.kind : 0,
      startTimeUnixNano: hrTimeToNano(span.startTime),
      endTimeUnixNano: hrTimeToNano(span.endTime),
      attributes: Object.entries(span.attributes || {}).map(([key, value]) =>
        toOtlpAnyValue(key, value)
      ),
      status: {
        code: statusCode,
        message: (span.status && span.status.message) || undefined
      },
      // OTEL JS SDK 2.x stores parent on parentSpanContext (parentSpanId is gone).
      ...(parentSpanId ? { parentSpanId } : {}),
      ...(events.length ? { events } : {})
    };
  });

  return {
    resourceSpans: [
      {
        resource: {
          attributes: [
            {
              key: ATTR_SERVICE_NAME,
              value: { stringValue: getOtelServiceName() }
            },
            {
              key: ATTR_SERVICE_VERSION,
              value: { stringValue: getOtelServiceVersion() }
            },
            {
              key: OTEL_ATTR_DEPLOYMENT_ENVIRONMENT,
              value: { stringValue: getOtelEnvironment() }
            }
          ]
        },
        scopeSpans: [
          {
            scope: {
              name: 'paperfly-pwa',
              version: getOtelServiceVersion()
            },
            spans: otlpSpans
          }
        ]
      }
    ]
  };
}

/**
 * Fetch/XHR auto-spans: allowlist Paperfly backends only.
 * Everything else (GA, CDN, static assets) is ignored — we do not need a span
 * per browser GET/POST for product traces.
 */
function buildHttpIgnoreUrls() {
  const allowedHosts = new Set(['api.paperfly.io', 'auth.paperfly.io']);
  [
    process.env.REACT_APP_API_GATEWAY_ENDPOINT,
    process.env.REACT_APP_AUTH_URL
  ].forEach(raw => {
    if (!raw || String(raw).includes('${')) return;
    try {
      allowedHosts.add(new URL(raw).hostname);
    } catch (e) {
      // ignore invalid
    }
  });

  const hostPattern = Array.from(allowedHosts)
    .map(h => h.replace(/\./g, '\\.'))
    .join('|');

  return [
    // Ignore any URL that is not an allowed Paperfly host
    new RegExp(`^(?!https?:\\/\\/(${hostPattern})(?::\\d+)?\\/).+`, 'i'),
    /otel\.paperfly\.io/i,
    /localhost:4318/i,
    /127\.0\.0\.1:4318/i
  ];
}

function buildPropagateTraceHeaderCorsUrls() {
  const hosts = new Set(['api.paperfly.io', 'auth.paperfly.io']);
  [
    process.env.REACT_APP_API_GATEWAY_ENDPOINT,
    process.env.REACT_APP_AUTH_URL
  ].forEach(raw => {
    if (!raw || String(raw).includes('${')) return;
    try {
      hosts.add(new URL(raw).hostname);
    } catch (e) {
      // ignore
    }
  });
  return Array.from(hosts).map(
    h =>
      new RegExp(`^https?:\\/\\/${h.replace(/\./g, '\\.')}(?::\\d+)?\\/`, 'i')
  );
}

const IGNORE_HTTP_URLS = buildHttpIgnoreUrls();
const PROPAGATE_TRACE_HEADER_CORS_URLS = buildPropagateTraceHeaderCorsUrls();

/**
 * Instrumentation copies `propagateTraceHeaderCorsUrls` once via `{...config}`,
 * so a getter never re-runs. Skip inject entirely while cron poll quiet/drop is
 * active — stops paperfly-rest from creating hundreds of poll children.
 */
class PollQuietAwarePropagator {
  constructor(delegate) {
    this._delegate = delegate;
  }

  inject(ctx, carrier, setter) {
    try {
      if (isPollQuiet() || isPollDrop()) {
        return;
      }
    } catch (e) {
      // ignore
    }
    return this._delegate.inject(ctx, carrier, setter);
  }

  extract(ctx, carrier, getter) {
    return this._delegate.extract(ctx, carrier, getter);
  }

  fields() {
    return this._delegate.fields();
  }
}

/**
 * Batch processor that defers cron-poll spans: each tick replaces the hold
 * buffer; STOP_CRONJOB flushes only the last tick (see pollQuiet.js).
 */
class CronPollAwareBatchSpanProcessor {
  constructor(exporter, config) {
    this._inner = new BatchSpanProcessor(exporter, config);
    setCronPollFlushSink(span => this._inner.onEnd(span));
  }

  onStart(span, parentContext) {
    if (isPollDrop()) {
      try {
        span.setAttribute(OTEL_ATTR_CRON_POLL_DROP, true);
      } catch (e) {
        // ignore
      }
    } else if (isPollQuiet()) {
      try {
        span.setAttribute(OTEL_ATTR_CRON_POLL, true);
      } catch (e) {
        // ignore
      }
    }
    if (typeof this._inner.onStart === 'function') {
      this._inner.onStart(span, parentContext);
    }
  }

  onEnd(span) {
    const attrs = span.attributes || {};
    if (attrs[OTEL_ATTR_CRON_POLL_DROP] === true) {
      return;
    }
    if (attrs[OTEL_ATTR_CRON_POLL] === true) {
      holdCronPollSpan(span);
      return;
    }
    this._inner.onEnd(span);
  }

  forceFlush() {
    return this._inner.forceFlush();
  }

  shutdown() {
    return this._inner.shutdown();
  }
}

let started = false;

export function isTelemetryStarted() {
  return started;
}

/**
 * Initialize browser OTel (idempotent). Call as early as configured
 * (index.js); cookie consent is not required.
 */
export function startTelemetry() {
  if (started || !isOtelConfigured()) {
    return;
  }
  started = true;

  if (process.env.REACT_APP_ENV !== 'production') {
    diag.setLogger(new DiagConsoleLogger(), DiagLogLevel.WARN);
  }

  const url = getOtelTracesUrl();
  const authorization = normalizeBearerAuthorization(getOtelAuthorization());

  const resource = resourceFromAttributes({
    [ATTR_SERVICE_NAME]: getOtelServiceName(),
    [ATTR_SERVICE_VERSION]: getOtelServiceVersion(),
    [OTEL_ATTR_DEPLOYMENT_ENVIRONMENT]: getOtelEnvironment()
  });

  const traceExporter = new JsonOtlpTraceExporter(url, authorization);
  const provider = new WebTracerProvider({
    resource,
    spanProcessors: [
      new SessionParentSpanProcessor(),
      new RequestParamRSpanProcessor(),
      new SessionIdSpanProcessor(),
      new HttpErrorMessageSpanProcessor(),
      new CronPollAwareBatchSpanProcessor(traceExporter, {
        maxQueueSize: 100,
        maxExportBatchSize: 20,
        scheduledDelayMillis: 2000
      })
    ]
  });

  // Prefer journey ambient, else pwa.session, so Fetch/XHR nest in the session tree.
  const baseContextManager = new StackContextManager();
  baseContextManager.enable();
  const contextManager = {
    active() {
      const current = baseContextManager.active();
      if (trace.getSpan(current)) return current;
      try {
        const tracing = require('./tracing');
        const business =
          tracing.getAmbientContext && tracing.getAmbientContext();
        if (business) return business;
      } catch (e) {
        // ignore
      }
      try {
        const sessionTrace = require('./sessionTrace');
        const sessionCtx =
          sessionTrace.getSessionContext && sessionTrace.getSessionContext();
        if (sessionCtx) return sessionCtx;
      } catch (e) {
        // ignore
      }
      return current;
    },
    with(...args) {
      return baseContextManager.with(...args);
    },
    bind(...args) {
      return baseContextManager.bind(...args);
    },
    enable() {
      return baseContextManager.enable();
    },
    disable() {
      return baseContextManager.disable();
    }
  };

  // Pass PollQuietAwarePropagator here — setGlobalPropagator after register()
  // hits "Attempted duplicate registration of API: propagation".
  provider.register({
    contextManager,
    propagator: new PollQuietAwarePropagator(new W3CTraceContextPropagator())
  });

  // Keep for forceFlush on pagehide / handoff early-export
  window.__paperflyOtelProvider = provider;
  window.__paperflyOtelTraceExporter = traceExporter;
  // CORS allowlist for non-poll requests (cross-origin Paperfly API/auth).
  // Do not use a getter here — InstrumentationBase.setConfig spreads config and
  // freezes the value; poll quiet is handled by PollQuietAwarePropagator.
  const httpInstrumentationOptions = applyCustomAttributes => ({
    ignoreUrls: IGNORE_HTTP_URLS,
    propagateTraceHeaderCorsUrls: PROPAGATE_TRACE_HEADER_CORS_URLS,
    applyCustomAttributesOnSpan: applyCustomAttributes
  });

  registerInstrumentations({
    instrumentations: [
      new FetchInstrumentation(
        httpInstrumentationOptions((span, _request, result) => {
          enrichFetchErrorSpan(span, result);
        })
      ),
      new XMLHttpRequestInstrumentation(
        httpInstrumentationOptions((span, xhr) => {
          enrichXhrErrorSpan(span, xhr);
        })
      )
    ]
  });

  window.addEventListener(
    'error',
    event => {
      const target = event.target;
      if (
        target &&
        target !== window &&
        (target.src || target.href || target.currentSrc)
      ) {
        const resourceUrl = String(
          target.currentSrc || target.src || target.href || ''
        );
        if (!resourceUrl || IGNORE_HTTP_URLS.some(re => re.test(resourceUrl))) {
          return;
        }
        const offline = typeof navigator !== 'undefined' && !navigator.onLine;
        const message = offline
          ? `Failed to load resource: net::ERR_INTERNET_DISCONNECTED ${resourceUrl}`
          : `Failed to load resource: ${resourceUrl}`;
        recordAppError('resource.error', message, {
          'error.url': resourceUrl,
          'error.resource_type': (target.tagName || '').toLowerCase(),
          'app.online': !offline
        });
        return;
      }

      if (isBrowserNoiseError(event.message, event.filename)) {
        recordAnalyticsEvent('APP', 'BROWSER_NOISE', event.message);
        return;
      }

      recordAppError('window.onerror', event.message || 'error', {
        'error.filename': event.filename || '',
        'error.lineno': event.lineno || 0,
        'app.online': typeof navigator !== 'undefined' ? navigator.onLine : true
      });
    },
    true
  );

  window.addEventListener('unhandledrejection', event => {
    const reason =
      event.reason instanceof Error
        ? event.reason.message
        : String(event.reason != null ? event.reason : 'rejection');
    if (isBrowserNoiseError(reason)) {
      recordAnalyticsEvent('APP', 'BROWSER_NOISE', reason);
      return;
    }
    recordAppError('unhandledrejection', reason, {
      'app.online': typeof navigator !== 'undefined' ? navigator.onLine : true
    });
  });

  if (process.env.REACT_APP_ENV !== 'production') {
    console.log(
      '[otel] started →',
      url,
      authorization ? '(Bearer auth set)' : '(no auth)'
    );
  }
}

/** Flush pending spans (e.g. on pagehide). Also early-exports open roots. */
export function forceFlushTelemetry() {
  try {
    const { flushLastCronPollSpans } = require('./pollQuiet');
    flushLastCronPollSpans();
  } catch (e) {
    // ignore
  }
  const provider =
    typeof window !== 'undefined' && window.__paperflyOtelProvider;
  const flushEnded =
    provider && typeof provider.forceFlush === 'function'
      ? provider.forceFlush()
      : Promise.resolve();

  return Promise.resolve(flushEnded).then(() => {
    // BatchSpanProcessor only queues ended spans — open handoff/session never
    // flush on their own. Push synthetics so QR parents exist before guest load.
    exportOpenTraceRootsIfNeeded();
  });
}

function exportOpenTraceRootsIfNeeded() {
  const exporter =
    typeof window !== 'undefined' && window.__paperflyOtelTraceExporter;
  if (!exporter || typeof exporter.export !== 'function') return;

  let needsExport = false;
  try {
    const sessionTrace = require('./sessionTrace');
    if (
      sessionTrace.shouldInjectSessionRoot &&
      sessionTrace.shouldInjectSessionRoot([])
    ) {
      needsExport = true;
    }
  } catch (e) {
    // ignore
  }
  try {
    const tracing = require('./tracing');
    if (
      tracing.hasOpenJourneysNeedingExport &&
      tracing.hasOpenJourneysNeedingExport()
    ) {
      needsExport = true;
    }
  } catch (e) {
    // ignore
  }
  if (!needsExport) return;

  exporter.export([], () => {});
}

export function recordAppError(name, message, attributes) {
  if (!started) return;
  try {
    const sessionTrace = require('./sessionTrace');
    const run = sessionTrace.runWithSessionContext || (fn => fn());
    run(() => {
      let parentCtx = context.active();
      try {
        const tracing = require('./tracing');
        const ambient =
          tracing.getAmbientContext && tracing.getAmbientContext();
        if (ambient) parentCtx = ambient;
      } catch (e) {
        const sessionCtx =
          sessionTrace.getSessionContext && sessionTrace.getSessionContext();
        if (sessionCtx) parentCtx = sessionCtx;
      }
      const tracer = trace.getTracer('paperfly-pwa');
      const span = tracer.startSpan(name, {}, parentCtx);
      span.setStatus({ code: SpanStatusCode.ERROR, message });
      span.setAttribute('error.message', message);
      const sessionId =
        (typeof window !== 'undefined' && window.pwaSession) || '';
      if (sessionId) span.setAttribute('pwa.session_id', sessionId);
      const requestId = getRequestParamR();
      if (requestId) span.setAttribute(OTEL_ATTR_REQUEST_R, requestId);
      Object.keys(attributes || {}).forEach(k => {
        span.setAttribute(k, attributes[k]);
      });
      span.end();
    });
  } catch (e) {
    // ignore
  }
}

/**
 * Application diary from gae*:
 * - app.layer=diary → short child under ambient journey (else session) + root event
 * - app.layer=ui    → session root event only (no Jaeger tree row)
 * Safe with session-root injection + SessionParentSpanProcessor (no rogue roots).
 */
export function recordAnalyticsEvent(
  category,
  action,
  label,
  eventValue,
  nonInteraction
) {
  if (!started) return;
  try {
    const { isPollQuiet } = require('./pollQuiet');
    // Intermediate cron polls: skip diary (HTTP held separately for last tick).
    if (isPollQuiet && isPollQuiet()) {
      return;
    }

    const {
      APP_LAYER,
      OTEL_ATTR_APP_LAYER,
      classifyAnalyticsLayer,
      isAnalyticsErrorEvent,
      getAnalyticsErrorMessage
    } = require('./analyticsLayer');
    const layer = classifyAnalyticsLayer(category, action);
    const isErrorEvent = isAnalyticsErrorEvent(category, action, label);

    const attrs = {
      'app.category': String(category),
      'app.action': String(action),
      'app.online': navigator.onLine,
      [OTEL_ATTR_APP_LAYER]: layer
    };
    if (isErrorEvent) {
      attrs['app.is_error'] = true;
    }
    if (label !== undefined) attrs['app.label'] = String(label).slice(0, 500);
    if (eventValue !== undefined) {
      attrs['app.value'] = String(eventValue).slice(0, 500);
    }
    if (nonInteraction !== undefined) {
      attrs['app.non_interaction'] = Boolean(nonInteraction);
    }

    let sessionId = '';
    let ambientCtx = null;
    try {
      const tracing = require('./tracing');
      sessionId = (tracing.getSessionId && tracing.getSessionId()) || '';
      ambientCtx =
        (tracing.getAmbientContext && tracing.getAmbientContext()) || null;
      const ambientSpan =
        tracing.getAmbientRecordingSpan && tracing.getAmbientRecordingSpan();
      if (ambientSpan && ambientSpan.spanContext) {
        const sc = ambientSpan.spanContext();
        if (sc && sc.spanId) {
          attrs['app.related_span_id'] = sc.spanId;
        }
      }
    } catch (e) {
      // ignore
    }
    if (!sessionId && typeof window !== 'undefined' && window.pwaSession) {
      sessionId = window.pwaSession;
    }
    if (sessionId) attrs['pwa.session_id'] = sessionId;

    const requestId = getRequestParamR();
    if (requestId) attrs[OTEL_ATTR_REQUEST_R] = requestId;

    const sessionTrace = require('./sessionTrace');
    if (
      !sessionTrace.isSessionTraceActive ||
      !sessionTrace.isSessionTraceActive()
    ) {
      // Pre-session boot: drop — do not create orphan app.* trace roots.
      return;
    }

    const name =
      action !== undefined && action !== null && String(action) !== ''
        ? `app.${category}.${action}`
        : `app.${category}`;
    // Always mirror on session root Logs (timeline), including UI chrome.
    sessionTrace.recordSessionEvent(name, attrs);

    if (layer === APP_LAYER.UI) {
      // UI chrome: no tree row — keeps Jaeger expandable by journey.
      return;
    }

    // Process diary: nest under open journey when present, else session.
    const parentCtx = ambientCtx || sessionTrace.getSessionContext();
    sessionTrace.withChildSpan(parentCtx, name, span => {
      if (!span) return;
      Object.keys(attrs).forEach(k => span.setAttribute(k, attrs[k]));
      if (isErrorEvent) {
        const message = getAnalyticsErrorMessage(category, action, label);
        span.setAttribute('error.message', message);
        span.setStatus({ code: SpanStatusCode.ERROR, message });
      }
    });
  } catch (e) {
    // ignore
  }
}
